// src/components/LeftBar/lefbar.jsx
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./leftbar.css";
import { axiosInstance } from "../../config";
import { fetchNotifications, openNotifStream } from "../../NotificationsApi";
import { AuthContext } from "../../context/AuthContext";
import { isAdminUser } from "../../utils/analytics";

/* ---------- Small blue verified check (Twitter-like) ---------- */
function VerifiedCheck({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#1DA1F2" />
      <path d="M17 9l-6.2 6L7 11.5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Unread badge (desktop) ---------- */
function UnreadBadge({ count }) {
  if (!count) return null;
  const c = count > 99 ? "99+" : String(count);
  const wrap = {
    marginLeft: 8,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    padding: "0 6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    background: "#ff3b30",
    color: "#fff",
    lineHeight: 1,
  };
  return <span style={wrap}>{c}</span>;
}

/* Auto-fit helper: gently shrink long names so they fit one line */
function fitNameStyle(name) {
  const n = (name || "").length;
  if (n <= 12) return { fontSize: 14 };
  if (n <= 16) return { fontSize: 13 };
  if (n <= 20) return { fontSize: 12 };
  return { fontSize: 11.5 }; // extra long
}


/* --------- storage helpers (same as MobileBottomBar) --------- */
function readCount() {
  const n = Number(localStorage.getItem("yzy_unread_count") || "0");
  return Number.isFinite(n) ? n : 0;
}
function writeCount(next, viewing) {
  const val = viewing ? 0 : Math.max(0, Number(next) || 0);
  localStorage.setItem("yzy_unread_count", String(val));
  window.dispatchEvent(new Event("yzy_unread_update"));
  return val;
}

/* --------- Icons --------- */
function BellIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}
function ClipsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 4a2 2 0 0 0-2 2v1h20V6a2 2 0 0 0-2-2H4zM2 9v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9H2zm8 2.5l5 3-5 3v-6z" />
    </svg>
  );
}
function SearchIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-3.5-3.5" />
    </svg>
  );
}
function ReelsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM5 5h2v2H5V5zm0 4h14v10H5V9zm12-2h2v2h-2V5zm-4 0h2v2h-2V5zm-4 0h2v2H9V5z"/>
    </svg>
  );
}
function CampaignsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <line x1="12" y1="12" x2="12" y2="16" />
      <line x1="10" y1="14" x2="14" y2="14" />
    </svg>
  );
}

function DiscoverIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" stroke="none"/>
    </svg>
  );
}

function UserIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm-7 10a7 7 0 0 1 14 0H5z" />
    </svg>
  );
}
/* Person + left arrow (matches your screenshot vibe) */
function PersonArrowLeftIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* head */}
      <circle cx="9" cy="7" r="3" />
      {/* shoulders/chest */}
      <path d="M4 21a7 7 0 0 1 10-6" />
      {/* left arrow */}
      <path d="M21 12h-7" />
      <path d="M16 9l-3 3 3 3" />
    </svg>
  );
}

const Leftbar = () => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const viewingNotifs =
    pathname.startsWith("/notifications") || pathname.startsWith("/notificationspage");

  /* ---------- Desktop Notifications state (same logic as MobileBottomBar) ---------- */
  const [unread, setUnread] = useState(readCount);
  const esRef = useRef(null);
  const pollRef = useRef(null);
  const fastPollRef = useRef(false);
  const seenIdsRef = useRef(new Set());
  const reconcileTimerRef = useRef(null);
  const mountedRef = useRef(false);

  const applyUnread = (n) => {
    const val = writeCount(n, viewingNotifs);
    setUnread(viewingNotifs ? 0 : val);
  };

  const reconcile = async () => {
    try {
      const data = await fetchNotifications({ limit: 50 });
      const items = Array.isArray(data?.items) ? data.items : [];
      const next = items.filter((i) => !i.read).length;
      applyUnread(next);
      const s = new Set();
      for (const it of items) {
        const id = it._id || it.id;
        if (id) s.add(String(id));
        if (s.size > 200) break;
      }
      seenIdsRef.current = s;
    } catch { /* ignore */ }
  };

  const ensurePolling = (ms) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(reconcile, ms);
  };

  // useEffect(() => {
  //   mountedRef.current = true;

  //   // SSE open (same helper)
  //   const es = openNotifStream();
  //   esRef.current = es;

  //   const onMessage = (evt) => {
  //     if (!mountedRef.current) return;
  //     fastPollRef.current = false;
  //     ensurePolling(25000);

  //     try {
  //       const msg = JSON.parse(evt.data || "{}");

  //       if (typeof msg.unreadCount === "number") {
  //         applyUnread(msg.unreadCount);
  //         return;
  //       }
  //       if (Array.isArray(msg.items)) {
  //         const next = msg.items.filter((i) => !i.read).length;
  //         applyUnread(next);
  //         const s = seenIdsRef.current;
  //         for (const it of msg.items) {
  //           const id = it._id || it.id;
  //           if (id) s.add(String(id));
  //           if (s.size > 200) break;
  //         }
  //         return;
  //       }
  //       if (Array.isArray(msg.readIds) && msg.readIds.length) {
  //         applyUnread(readCount() - msg.readIds.length);
  //         return;
  //       }
  //       const one = msg.item || msg.notification;
  //       if (one) {
  //         const id = String(one._id || one.id || "");
  //         const already = id && seenIdsRef.current.has(id);
  //         if (id) {
  //           seenIdsRef.current.add(id);
  //           if (seenIdsRef.current.size > 300) {
  //             seenIdsRef.current = new Set(
  //               Array.from(seenIdsRef.current).slice(-200)
  //             );
  //           }
  //         }
  //         if (!one.read && !already && !viewingNotifs) {
  //           applyUnread(readCount() + 1);
  //         } else {
  //           if (!reconcileTimerRef.current) {
  //             reconcileTimerRef.current = setTimeout(() => {
  //               reconcileTimerRef.current = null;
  //               reconcile();
  //             }, 1000);
  //           }
  //         }
  //         return;
  //       }

  //       // unknown shape → reconcile soon
  //       if (!reconcileTimerRef.current) {
  //         reconcileTimerRef.current = setTimeout(() => {
  //           reconcileTimerRef.current = null;
  //           reconcile();
  //         }, 1000);
  //       }
  //     } catch {
  //       if (!reconcileTimerRef.current) {
  //         reconcileTimerRef.current = setTimeout(() => {
  //           reconcileTimerRef.current = null;
  //           reconcile();
  //         }, 1000);
  //       }
  //     }
  //   };

  //   es.onmessage = onMessage;

  //   es.onerror = () => {
  //     // Stream died → fast polling fallback
  //     if (!fastPollRef.current) {
  //       fastPollRef.current = true;
  //       ensurePolling(5000);
  //     }
  //   };

  //   // initial reconcile + normal polling
  //   reconcile();
  //   ensurePolling(25000);

  //   // refresh when tab focused/visible
  //   const onFocus = () => reconcile();
  //   const onVis = () => { if (document.visibilityState === "visible") reconcile(); };
  //   window.addEventListener("focus", onFocus);
  //   document.addEventListener("visibilitychange", onVis);

  //   return () => {
  //     mountedRef.current = false;
  //     if (esRef.current) { try { esRef.current.close(); } catch {} esRef.current = null; }
  //     if (pollRef.current) clearInterval(pollRef.current);
  //     if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
  //     window.removeEventListener("focus", onFocus);
  //     document.removeEventListener("visibilitychange", onVis);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // sync with other tabs/pages
  useEffect(() => {
    const onStorage = (e) => { if (e.key === "yzy_unread_count") setUnread(readCount()); };
    const onCustom  = () => setUnread(readCount());
    window.addEventListener("storage", onStorage);
    window.addEventListener("yzy_unread_update", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("yzy_unread_update", onCustom);
    };
  }, []);

  /* ---------- New-clip badge on the Library tab (desktop) ----------
     ForYouPage writes cb_new_clips + fires the event as clips spawn in, and
     clears it to 0 when the Library tab is opened. We just reflect it here. */
  const [newClips, setNewClips] = useState(() => {
    const n = Number(localStorage.getItem("cb_new_clips") || "0");
    return Number.isFinite(n) ? n : 0;
  });
  useEffect(() => {
    const read = () => {
      const n = Number(localStorage.getItem("cb_new_clips") || "0");
      setNewClips(Number.isFinite(n) ? n : 0);
    };
    window.addEventListener("cb_new_clips", read);
    window.addEventListener("storage", read);
    return () => {
      window.removeEventListener("cb_new_clips", read);
      window.removeEventListener("storage", read);
    };
  }, []);

  // visiting notifications clears immediately
  useEffect(() => {
    if (viewingNotifs) applyUnread(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingNotifs]);

  /* ---------- Live state poller removed — not a streaming app ---------- */

  return (
    <div className="leftbar">
      {/* HOME with icon */}
      <a data-paywall-allow="" href="/home" className="tab tab-link" target="_self" rel="noreferrer" title="Clip" aria-label="Clip">
        <ClipsIcon size={24} />
        <span>Clip</span>
      </a>

      {/* LIBRARY */}
      <a data-paywall-allow="" href="/home?tab=library" className="tab tab-link" target="_self" rel="noreferrer" title="Library" aria-label="Library">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        <span>Library</span>
        <UnreadBadge count={newClips} />
      </a>

      {/* ANALYTICS — admin-only until TikTok approves the app */}
      {isAdminUser(user) && (
        <a href="/analytics" className="tab tab-link" target="_self" rel="noreferrer" title="Analytics" aria-label="Analytics">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
          </svg>
          <span>Analytics</span>
        </a>
      )}

      {/* PROFILE */}
      <a href="/me" className="tab tab-link" target="_self" rel="noreferrer" title={t('profileTab')} aria-label={t('profileTab')}>
        <UserIcon size={24} />
        <span>{t('profileTab')}</span>
      </a>
    </div>
  );
};

export default Leftbar;
